import { placeholderScript } from './placeholder-script';
import { uploadFile } from './upload-file';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';
import type { ProStudioContext } from '../xstate/prostudio.xstate.types';

export { placeholderScript };

/**
 * How long one placeholder upload may stay silent before it counts as failed.
 * The script is a few hundred bytes, so a minute is not a slow upload, it is a
 * connection that will not answer (PSTUDIO-4458's shape).
 */
export const PLACEHOLDER_UPLOAD_TIMEOUT_MS = 60_000;

/**
 * Upload a placeholder for each command, one at a time, to the open draft —
 * the same endpoint and draft update as Actions' own upload
 * (`use-upload-python-files.ts`), without the hook. Resolves with the
 * commands that uploaded. An upload that answers nothing within
 * `PLACEHOLDER_UPLOAD_TIMEOUT_MS` is aborted and skipped, so one silent
 * request cannot hold the rest of the loop (and the caller) forever; an answer
 * that arrives after that is ignored.
 */
export const uploadPlaceholderScripts = async (
  commands: { command: string; decisionVariable: string; answer?: string }[]
): Promise<string[]> => {
  const ctx = proStudioService.getSnapshot().context;
  const projectId = ctx.project?.id;
  const versionId =
    ctx.customerId && ctx.botName && ctx.draftVersion
      ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
      : undefined;
  if (!projectId || !versionId || !commands.length) return [];

  const uploaded: string[] = [];
  for (const { command, decisionVariable, answer } of commands) {
    const file = new File(
      [placeholderScript(command, decisionVariable, answer)],
      `${command}.py`,
      { type: 'text/x-python' }
    );
    // eslint-disable-next-line no-await-in-loop -- one at a time, like Actions
    await new Promise<void>((resolve) => {
      let settled = false;
      // An object, not a `let`: a synchronous answer settles before the timer
      // is armed, and `settle` must still see the id it has to clear.
      const timer: { id?: ReturnType<typeof setTimeout> } = {};
      const settle = () => {
        if (settled) return false;
        settled = true;
        clearTimeout(timer.id);
        resolve();
        return true;
      };
      const request = uploadFile({
        url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/scripts`,
        file: [file],
        type: 'scriptFile',
        onChange: ({ data }) => {
          if (!settle()) return;
          sendProStudioEvent({
            type: 'updateDraftData',
            draft:
              (data as ProStudioContext['draft']) ||
              proStudioService.getSnapshot().context.draft,
            action: 'upload',
          });
          uploaded.push(command);
        },
        error: (error) => {
          if (!settle()) return;
          console.error('[placeholder-scripts] upload failed', command, error);
        },
      });
      // Armed after the call: a synchronous answer has already settled.
      if (!settled) {
        timer.id = setTimeout(() => {
          // `settle` clears this timer, so it only fires for an unsettled upload.
          settle();
          console.error('[placeholder-scripts] upload did not answer', command);
          // Aborting makes the request fire `error`, which `settle` ignores.
          request?.abort?.();
        }, PLACEHOLDER_UPLOAD_TIMEOUT_MS);
      }
    });
  }
  return uploaded;
};
